import {
  Bookmarks,
  Pause,
  PlayArrow,
  Replay30,
  SkipNext,
  SkipPrevious,
} from '@mui/icons-material';
import { Box, CircularProgress, Hidden, IconButton } from '@mui/material';

import { ComplianceTypes, Modals, PLAYER_REPLAY_30 } from '@/constants';
import { useBookmarkManagement, useDebouncedValue } from '@/hooks';
import { thumbsFeedback } from '@/images';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  playerSessionsSelectors,
  playerThunks,
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectChapterProgress,
  selectEpisodeCompliance,
  selectIsChangingChapter,
  selectIsPlayerBuffering,
  selectIsPlayerPlaying,
  selectIsPlayerSeeking,
  selectPersistedPlayerSpeedPreference,
  selectPlaybackSpeedOptions,
  selectSessionUserId,
  uiActions,
  useAppDispatch,
  useAppSelector,
  useFetchChapterQuery,
  useFetchEpisodeChaptersQuery,
  useFetchEpisodeQuery,
  userPreferencesActions,
} from '@/store';

import { QuizReadyTag } from './components';

export const MediaControls = () => {
  const dispatch = useAppDispatch();

  const userId = useAppSelector(selectSessionUserId);
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId)!;
  const activeChapterId = useAppSelector(selectActiveChapterId)!;
  const playerSpeed = useAppSelector(selectPersistedPlayerSpeedPreference);
  const isBuffering = useAppSelector(selectIsPlayerBuffering);
  const isSeeking = useAppSelector(selectIsPlayerSeeking);
  const isPlaying = useAppSelector(selectIsPlayerPlaying);
  const isChangingChapter = useAppSelector(selectIsChangingChapter);
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );
  const playerSession = useAppSelector(state =>
    playerSessionsSelectors.selectById(state, activeEpisodeId),
  );
  const playbackSpeedOptions = useAppSelector(selectPlaybackSpeedOptions);

  const isCPE = compliance === ComplianceTypes.CPE;

  const { data: episode } = useFetchEpisodeQuery({
    episodeId: activeEpisodeId,
    compliance,
  });
  const { data: chapters } = useFetchEpisodeChaptersQuery({
    episodeId: activeEpisodeId,
    compliance,
  });
  const { data: chapter } = useFetchChapterQuery({
    chapterId: activeChapterId,
    compliance,
  });

  const chapterPosition =
    chapters?.find(({ chapterId }) => chapterId === activeChapterId)
      ?.chapterPosition ?? -1;
  const nextChapter = chapters?.find(
    current => current.chapterPosition === chapterPosition + 1,
  );
  const previousChapter = chapters?.find(
    current => current.chapterPosition === chapterPosition - 1,
  );
  const isChapterTrailer = activeChapterId === episode?.trailerId;

  // Selects the chapter data from the EntityAdapter as it gets continuously updated for e.g. set_position
  const chapterProgress = useAppSelector(state =>
    selectChapterProgress(state, {
      episodeId: activeEpisodeId,
      chapterId: activeChapterId,
    }),
  );

  const showLoadingIndicator = useDebouncedValue(
    isBuffering || isChangingChapter,
    1000,
  );

  const location = {
    component: EVENT_CONSTANTS.COMPONENT.TRACK_PLAYER,
  };

  const { toggleBookmark, isBookmarking, isBookmarked } = useBookmarkManagement(
    {
      episodeId: activeEpisodeId,
      location: {
        ...location,
        button: EVENT_CONSTANTS.BUTTON.BOOKMARK,
      },
      referrer: location,
    },
  );

  const togglePlay = () => {
    dispatch(
      isPlaying ? playerThunks.pausePlayer() : playerThunks.playPlayer(),
    );

    if (chapter) {
      AnalyticsService.chapterPause({
        chapterId: activeChapterId,
        episodeId: activeEpisodeId,
        location: {
          ...location,
          button: EVENT_CONSTANTS.BUTTON.PLAY,
        },
        referrer: location,
        pausing: isPlaying,
      });

      AnalyticsService.trackPlayerPlayPause({
        chapterId: activeChapterId,
        episodeId: activeEpisodeId,
        location: {
          ...location,
          button: EVENT_CONSTANTS.BUTTON.PLAY,
        },
        referrer: location,
        pausing: isPlaying,
      });
    }
  };

  const playPrevious = () => {
    if (previousChapter) {
      AnalyticsService.trackPlayerSkip({
        chapterId: activeChapterId,
        episodeId: activeEpisodeId,
        direction: EVENT_CONSTANTS.BUTTON.SKIP_PREVIOUS,
      });
      dispatch(
        playerThunks.chapterPlayPressed({
          activeChapter: {
            episodeId: activeEpisodeId,
            chapterId: previousChapter.chapterId,
          },
          location: {
            ...location,
            button: EVENT_CONSTANTS.BUTTON.SKIP_PREVIOUS,
          },
          referrer: location,
        }),
      );
    }
  };

  const playNext = () => {
    if (nextChapter) {
      AnalyticsService.trackPlayerSkip({
        chapterId: activeChapterId,
        episodeId: activeEpisodeId,
        direction: EVENT_CONSTANTS.BUTTON.SKIP_NEXT,
      });
      dispatch(
        playerThunks.chapterPlayPressed({
          activeChapter: {
            episodeId: activeEpisodeId,
            chapterId: nextChapter.chapterId,
          },
          location: {
            ...location,
            button: EVENT_CONSTANTS.BUTTON.SKIP_NEXT,
          },
          referrer: location,
        }),
      );
    }
  };

  const setPlayerSpeedToNextOption = () => {
    const currentSpeedIndex = playbackSpeedOptions.indexOf(playerSpeed);
    const isCurrentSpeedLastOption =
      playbackSpeedOptions.length - 1 === currentSpeedIndex;
    const nextSpeedIndex = isCurrentSpeedLastOption ? 0 : currentSpeedIndex + 1;
    const nextSpeed = playbackSpeedOptions[nextSpeedIndex];

    AnalyticsService.playbackSpeedChanged({
      speed: nextSpeed,
      chapterId: activeChapterId,
      episodeId: activeEpisodeId,
      location: {
        ...location,
        button: EVENT_CONSTANTS.BUTTON.SPEED,
      },
    });

    dispatch(userPreferencesActions.setPlaybackSpeedPreference(nextSpeed));
  };

  const seekThirtySecondsBack = () => {
    AnalyticsService.trackPlayerRewind({
      chapterId: activeChapterId,
      episodeId: activeEpisodeId,
      location: { ...location, button: EVENT_CONSTANTS.BUTTON.SEEK_THIRTY },
    });
    dispatch(playerThunks.seekToPosition({ delta: PLAYER_REPLAY_30 }));
  };

  return (
    <Box sx={styles.mediaControlsContainer}>
      {!isChapterTrailer && (
        <IconButton
          sx={[styles.iconButton, styles.desktopButton]}
          onClick={toggleBookmark}
          disabled={isBookmarking}
          aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark'}>
          <Bookmarks sx={[styles.icon, isBookmarked && styles.activeIcon]} />
        </IconButton>
      )}
      <IconButton
        sx={[styles.iconButton, styles.desktopButton]}
        onClick={seekThirtySecondsBack}
        disabled={isSeeking}
        aria-label="Back 30 seconds">
        <Replay30 sx={styles.icon} />
      </IconButton>
      <IconButton
        sx={[styles.iconButton, styles.tabletButton]}
        onClick={playPrevious}
        disabled={isChapterTrailer || !previousChapter}
        aria-label="Previous">
        <SkipPrevious sx={styles.icon} />
      </IconButton>
      {showLoadingIndicator ? (
        <CircularProgress sx={styles.circularProgressIndicator} />
      ) : (
        <IconButton
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? (
            <Pause sx={[styles.playPauseIcon, styles.iconSelected]} />
          ) : (
            <PlayArrow sx={[styles.playPauseIcon, styles.iconSelected]} />
          )}
        </IconButton>
      )}
      <IconButton
        sx={[styles.iconButton, styles.tabletButton]}
        onClick={playNext}
        disabled={isChapterTrailer || !nextChapter}
        aria-label="Next">
        <SkipNext sx={styles.icon} />
      </IconButton>
      <IconButton
        sx={[styles.iconButton, styles.desktopButton]}
        disabled={!userId}
        onClick={setPlayerSpeedToNextOption}
        aria-label={`${playerSpeed}x speed`}>
        <Box sx={styles.playbackSpeedIcon} component="span">
          {playerSpeed}x
        </Box>
      </IconButton>
      {userId && (
        <IconButton
          sx={[styles.iconButton, styles.desktopButton]}
          aria-label="Feedback"
          disabled={!playerSession}
          onClick={() => {
            dispatch(
              uiActions.setActiveModal({
                name: Modals.EPISODE_FEEDBACK,
                params: {
                  episodeId: activeEpisodeId,
                  chapterId: activeChapterId,
                },
              }),
            );

            AnalyticsService.trackPlayerFeedback({
              chapterId: activeChapterId,
              episodeId: activeEpisodeId,
            });
          }}>
          <Box
            component="img"
            sx={styles.iconButton}
            src={thumbsFeedback}
            alt="Episode Feedback"
          />
        </IconButton>
      )}
      <Hidden xlDown>
        <Box ml={5}>
          {!isChapterTrailer &&
            !isCPE &&
            chapterProgress?.state === 'quiz_ready' && (
              <QuizReadyTag
                episodeId={activeEpisodeId}
                chapterId={activeChapterId}
                referrer={{
                  page: 'player',
                  component: EVENT_CONSTANTS.COMPONENT.MINI_PLAYER,
                }}
              />
            )}
        </Box>
      </Hidden>
    </Box>
  );
};

const styles = {
  mediaControlsContainer: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    margin: '0 auto',
    pt: 0.5,
  },
  iconButton: {
    height: 56,
    width: 56,
  },
  playPauseIcon: {
    fontSize: {
      xs: 40,
      sm: 60,
    },
  },
  desktopButton: {
    display: {
      xs: 'none',
      md: 'initial',
    },
  },
  tabletButton: {
    display: {
      xs: 'none',
      sm: 'initial',
    },
  },
  icon: {
    fontSize: 30,
    color: 'grey50',
  },
  activeIcon: {
    color: 'secondary.active',
  },
  playbackSpeedIcon: {
    fontSize: 20,
    color: 'grey50',
  },
  iconSelected: {
    color: 'secondary.active',
  },
  playEpisodeButton: {
    color: 'secondary.active',
    fontSize: 18,
  },
  circularProgressIndicator: {
    color: 'secondary.active',
    fontSize: 32,
  },
};
